import { classifyOptionSegment } from "./salesUtils";

/**
 * Shared presentation rules for product rows on the store operation pages
 * (Sales, Returns, Exchanges). Business rules stay on each page; this only
 * decides how an item is shown.
 */

/** Colour, size and any other genuinely differentiating option, from "Name: Value / ..." */
export function splitItemOptions(optionSummary) {
  const result = { color: null, size: null, others: [] };

  String(optionSummary || "")
    .split(" / ")
    .map((segment) => classifyOptionSegment(segment))
    .filter(Boolean)
    .forEach((option) => {
      if (option.kind === "color" && !result.color) {
        result.color = option.value;
      } else if (option.kind === "size" && !result.size) {
        result.size = option.value;
      } else if (option.kind === "other") {
        result.others.push(option);
      }
    });

  return result;
}

/**
 * Chips for an operational row: colour and size first, then any unknown
 * dimension that still differentiates the variant.
 *
 * The brand is intentionally NOT a chip: in this store every product shares the
 * brand, so repeating it on every row only adds noise. It stays in the data.
 */
export function buildItemOptionChips(optionSummary, t) {
  const { color, size, others } = splitItemOptions(optionSummary);

  return [
    color ? t("commerceCommon.optionColorLabel").replace("{value}", color) : null,
    size ? t("commerceCommon.optionSizeLabel").replace("{value}", size) : null,
    ...others.map((option) => (option.name ? `${option.name}: ${option.value}` : option.value)),
  ].filter(Boolean);
}

/**
 * The display fields a basket/replacement line keeps from an item. Scan and
 * manual search responses carry the same fields, so a line looks identical no
 * matter how the product was added.
 */
export function pickItemDisplayFields(item) {
  return {
    productTitle: item?.productTitle,
    variantTitle: item?.variantTitle,
    optionSummary: item?.optionSummary ?? null,
    brandName: item?.brandName ?? null,
    sku: item?.sku ?? null,
    variantBarcode: item?.variantBarcode ?? null,
    imageUrl: item?.imageUrl ?? null,
    thumbnailUrl: item?.thumbnailUrl ?? null,
    imageAltText: item?.imageAltText ?? null,
  };
}

/**
 * Only the media fields of an item, for places that already build their own line
 * (the Sales cart). The server has already picked the image - colour cover, then
 * variant image, then product cover - so a scanned and a searched item keep the
 * same thumbnail once they are in the cart.
 */
export function pickItemMediaFields(item) {
  return {
    imageUrl: item?.imageUrl ?? null,
    thumbnailUrl: item?.thumbnailUrl ?? null,
    imageAltText: item?.imageAltText ?? null,
  };
}

/** The real catalog image for an item, or null. Never a made-up URL. */
export function resolveItemImage(item) {
  return item?.thumbnailUrl || item?.imageUrl || null;
}

/** A double click on a real control inside a row must not also trigger the row action. */
export function isInteractiveTarget(target) {
  return Boolean(target?.closest?.("button, a, input, select, textarea, label"));
}
